// src/sizing/buildMcRequest.ts
import type { NewSizingRequest } from './newSizingEngine';
import { USE_NEW_PIPELINE } from './pipelineFlags';
import type { PersonaSeed } from '@/lib/repo';
import type { LatencyMode } from '@/types/legacy-stubs';

type ExtendedLatencyMode = LatencyMode | 'responsive' | 'queue' | 'none';

type BuildMcRequestParams = {
  personas: Array<PersonaSeed & Record<string, any>>;
  model: {
    id: string;
    paramsBillions: number;
    precisionBits: number;
    kvBytesPerToken: number;
    weightsGiB: number;
    weightsGiBUnshared?: number;
    usableKvGiBPerGpu: number;
    hbmGiB?: number;
    bridgeGroupSize?: number;
    fitsWeightsWithoutSharing?: boolean;
    fitsWeightsWithSharing?: boolean;
    weightsSharedWithinGroup?: boolean;
  };
  throughput: {
    promptTpsPerGpu: number;
    decodeTpsPerGpu: number;
  };
  uiState: {
    conversationTurns: number;
    batchSize: number;
    latencySloMs: number;
    mcFastTrials?: number;
    mcRefineTrials?: number;
    mcHorizonSeconds?: number;
    trafficVolatility?: number;
  };
  scenario: { concurrentUsers: number };
  latencyMode: ExtendedLatencyMode;
};

function resolveSloAlpha(mode: ExtendedLatencyMode): number {
  // Higher values mean stricter SLO, lower values mean more continuity / async
  switch (mode) {
    case 'snappy':
      return 0.9;
    case 'fast':
      return 0.7;
    case 'resp':
    case 'responsive':
      return 0.5;
    case 'comfy':
      return 0.25;
    case 'async':
    case 'queue':
    case 'none':
      return 0;
    default:
      // Balanced or unknown modes land in the middle
      return 0.4;
  }
}

function resolvePersonaLatency(p: PersonaSeed & Record<string, any>, mode: ExtendedLatencyMode) {
  // Prefer values already computed upstream in SizingPage
  const inlineTtftMs = typeof p.ttftMs === 'number' ? p.ttftMs : undefined;
  const inlineDecodeTps = typeof p.decodeTps === 'number' ? p.decodeTps : undefined;

  if (inlineTtftMs !== undefined || inlineDecodeTps !== undefined) {
    return {
      ttftSec: (inlineTtftMs ?? 0) / 1000,
      decodeTokPerSecPerUser: inlineDecodeTps ?? 0,
    };
  }

  // Fallback to profile based latency if inline values are not present
  const profile = p.latency_profiles?.[mode] ?? p.latency_profiles?.resp;
  const ttftMs =
    profile?.ttft_ms ??
    (p as any).default_latency_slo_ms ??
    (p as any).latency_slo_default_ms ??
    0;

  return {
    ttftSec: ttftMs / 1000,
    decodeTokPerSecPerUser: profile?.decode_tps ?? 0,
  };
}

export function buildMcRequest({
  personas,
  model,
  throughput,
  uiState,
  scenario,
  latencyMode,
}: BuildMcRequestParams): NewSizingRequest | null {
  if (!Array.isArray(personas) || personas.length === 0) {
    return null;
  }

  if (!USE_NEW_PIPELINE) {
    console.warn('[UnifiedSizing] USE_NEW_PIPELINE disabled; buildMcRequest will not proceed.');
  }

  const turnsPerSession = uiState.conversationTurns ?? 3;

  const req: NewSizingRequest = {
    baseConcurrentUsers: scenario.concurrentUsers,
    conversationTurns: turnsPerSession,
    batchSize: uiState.batchSize,
    latencySloMs: uiState.latencySloMs,
    perGpuPromptTps: throughput.promptTpsPerGpu,
    perGpuDecodeTps: throughput.decodeTpsPerGpu,
    personas: personas.map((p: PersonaSeed & any) => {
      const turnsKept = p.turnsPerSession ?? p.turns_kept ?? turnsPerSession ?? 1;
      const { ttftSec, decodeTokPerSecPerUser } = resolvePersonaLatency(p, latencyMode);

      // Prefer already computed per conversation totals if present
      const tokensInPerConversation =
        typeof p.inputTokensPerConversation === 'number'
          ? p.inputTokensPerConversation
          : typeof p.tokens?.input === 'number'
          ? p.tokens.input
          : (p.prompt_tokens_per_turn ?? p.inputTokensPerTurn ?? 0) * turnsKept;

      const tokensOutPerConversation =
        typeof p.outputTokensPerConversation === 'number'
          ? p.outputTokensPerConversation
          : typeof p.tokens?.output === 'number'
          ? p.tokens.output
          : (p.response_tokens_per_turn ?? p.outputTokensPerTurn ?? 0) * turnsKept;

      return {
        id: p.id,
        name: p.name,
        sharePercent: p.sharePercent,
        population: p.population,
        inputTokensPerConversation: tokensInPerConversation,
        outputTokensPerConversation: tokensOutPerConversation,
        readSeconds: p.readSec,
        thinkSeconds: p.thinkSec,
        ttftMs: (ttftSec ?? 0) * 1000,
        decodeTps: decodeTokPerSecPerUser ?? 0,
        turnsKept,
      };
    }),
    model: {
      id: model.id,
      paramsBillions: model.paramsBillions,
      precisionBits: model.precisionBits,
      kvBytesPerToken: model.kvBytesPerToken,
      weightsGiB: model.weightsGiB,
      weightsGiBUnshared: model.weightsGiBUnshared,
      usableKvGiBPerGpu: model.usableKvGiBPerGpu,
      hbmGiB: model.hbmGiB,
      bridgeGroupSize: model.bridgeGroupSize,
      fitsWeightsWithoutSharing: model.fitsWeightsWithoutSharing,
      fitsWeightsWithSharing: model.fitsWeightsWithSharing,
      weightsSharedWithinGroup: model.weightsSharedWithinGroup,
    },
    throughput: {
      promptTpsPerGpu: throughput.promptTpsPerGpu,
      decodeTpsPerGpu: throughput.decodeTpsPerGpu,
    },
    mcConfig: {
      fastTrials: uiState.mcFastTrials,
      refineTrials: uiState.mcRefineTrials,
      horizonSeconds: uiState.mcHorizonSeconds,
      trafficVolatility: uiState.trafficVolatility,
    },
    sloConfig: {
      alpha: resolveSloAlpha(latencyMode),
      uiOverheadMs: 250,
      maxPenalty: 4,
    },
  };

  console.debug(
    '[buildMcRequest] Prepared MC request personas',
    req.personas.map((p) => ({
      id: p.id,
      in: p.inputTokensPerConversation,
      out: p.outputTokensPerConversation,
      read: p.readSeconds,
      think: p.thinkSeconds,
      ttftMs: p.ttftMs,
      decodeTps: p.decodeTps,
      turnsKept: p.turnsKept,
    })),
  );
  return req;
}
